import type { ConfigRolloutState, ConfigScopeRef, SettingsPatch } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { getConfigLayer, getConfigRollout, putConfigLayer } from '../api/config.js';
import { listServerGroups } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { SCHEMA_DEFAULTS } from '../components/settings-defaults.js';
import { SettingsForm } from '../components/SettingsForm.js';
import { effectiveTimezone, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Settings (knowledge doc section 11).
 *
 * Two layers live here — the global defaults and one group's overrides — and the
 * third, a single server's, lives on that server's own page, which is where
 * somebody is when they are thinking about one machine.
 *
 * The rollout table underneath is section 11's "per-server status". It is the
 * second half of the promise: a setting that is saved but has reached nothing is
 * not a setting that has been changed, and this is where that becomes visible.
 */

const GLOBAL = '__global__';

export function SettingsPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  // The Groups page links here with its group chosen.
  const [params] = useSearchParams();
  const [scope, setScope] = useState(params.get('group') ?? GLOBAL);

  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const ref: ConfigScopeRef =
    scope === GLOBAL ? { scope: 'global' } : { scope: 'group', id: scope };

  return (
    <div className="page">
      <PageHeader
        title="Settings"
        description="What every server should be doing. The central database holds it; each agent makes its server match and reports back."
      />

      <Card
        title="Which layer"
        description="Later layers win: global defaults, then a group, then one server on its own page."
      >
        <SelectField
          label="Layer"
          value={scope}
          onChange={(event) => {
            setScope(event.target.value);
          }}
        >
          <option value={GLOBAL}>Global defaults — every server</option>
          {(groups.data ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} — {group.serverCount} server{group.serverCount === 1 ? '' : 's'}
            </option>
          ))}
        </SelectField>
      </Card>

      <LayerEditor key={scope} scopeRef={ref} />

      <RolloutCard timezone={timezone} />
    </div>
  );
}

function LayerEditor({ scopeRef }: { scopeRef: ConfigScopeRef }): ReactNode {
  const queryClient = useQueryClient();
  const key = ['config-layer', scopeRef.scope, scopeRef.scope === 'global' ? '' : scopeRef.id];

  const layer = useQuery({ queryKey: key, queryFn: () => getConfigLayer(scopeRef) });

  const [draft, setDraft] = useState<SettingsPatch>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ created: number; unchanged: number } | null>(null);

  useEffect(() => {
    // Seeded once the layer arrives, and again if it changes underneath — a revert
    // from somewhere else, or another admin saving.
    if (layer.data !== undefined) setDraft(layer.data.layer?.settings ?? {});
  }, [layer.data]);

  const save = useMutation({
    mutationFn: () => putConfigLayer(scopeRef, draft),
    onSuccess: (response) => {
      setError(null);
      setSaved(response.rebuild);
      void queryClient.invalidateQueries({ queryKey: ['config-layer'] });
      void queryClient.invalidateQueries({ queryKey: ['config-rollout'] });
    },
    onError: (caught: unknown) => {
      setSaved(null);
      setError(caught instanceof Error ? caught.message : 'Could not save those settings.');
    },
  });

  if (layer.isPending) {
    return <Loading />;
  }

  if (layer.isError) {
    return (
      <Alert tone="danger" title="Could not load that layer">
        Try again in a moment.
      </Alert>
    );
  }

  const reach = layer.data.affectedServers;

  return (
    <>
      <Alert tone="info">
        {reach === 0
          ? 'No servers are in this layer yet, so nothing changes until one is.'
          : `Saving changes ${String(reach)} server${reach === 1 ? '' : 's'}.`}
        {layer.data.layer !== null && (
          <>
            {' '}
            Last changed by {layer.data.layer.updatedByEmail ?? 'somebody since removed'}{' '}
            {formatRelative(layer.data.layer.updatedAt)}.
          </>
        )}
      </Alert>

      <SettingsForm
        settings={draft}
        onChange={setDraft}
        inherited={SCHEMA_DEFAULTS}
        disabled={save.isPending}
      />

      {error !== null && <Alert tone="danger">{error}</Alert>}

      {saved !== null && (
        <Alert tone="success" title="Saved">
          {saved.created === 0
            ? 'Nothing changed: every server was already on these settings.'
            : `${String(saved.created)} server${saved.created === 1 ? '' : 's'} will pick this up within seconds.`}
        </Alert>
      )}

      <div className="row">
        <Button
          variant="primary"
          busy={save.isPending}
          busyLabel="Saving…"
          onClick={() => {
            setSaved(null);
            save.mutate();
          }}
        >
          Save settings
        </Button>
      </div>
    </>
  );
}

const STATE_TONE: Record<ConfigRolloutState, string> = {
  applied: 'badge-success',
  pending: 'badge-accent',
  failed: 'badge-danger',
  drift: 'badge-warning',
};

const STATE_TITLE: Record<ConfigRolloutState, string> = {
  applied: 'Running the newest settings',
  pending: 'Has not confirmed the newest settings yet',
  failed: 'Could not apply them and went back to the last version that worked',
  drift: 'Its generated files no longer match what was written',
};

export function ConfigStateBadge({ state }: { state: ConfigRolloutState }): ReactNode {
  return (
    <span className={`badge ${STATE_TONE[state]}`} title={STATE_TITLE[state]}>
      {state}
    </span>
  );
}

function RolloutCard({ timezone }: { timezone: string }): ReactNode {
  const rollout = useQuery({ queryKey: ['config-rollout'], queryFn: getConfigRollout });

  return (
    <Card
      title="Where the settings have got to"
      description="Section 11's per-server status. Pending means the server has not confirmed the newest version yet."
    >
      {rollout.isPending && <Loading />}

      {rollout.data?.length === 0 && (
        <EmptyState title="No servers yet">
          Settings apply to servers that have joined the fleet.
        </EmptyState>
      )}

      {rollout.data !== undefined && rollout.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">Servers and the settings they are running</caption>
            <thead>
              <tr>
                <th scope="col">Server</th>
                <th scope="col">State</th>
                <th scope="col">Version</th>
                <th scope="col">Reported</th>
              </tr>
            </thead>
            <tbody>
              {rollout.data.map((row) => (
                <tr key={row.serverId}>
                  <th scope="row">
                    <Link to={`/servers/${row.serverId}`}>{row.name}</Link>
                  </th>
                  <td>
                    <ConfigStateBadge state={row.state} />
                    {row.detail !== null && <div className="muted text-sm">{row.detail}</div>}
                  </td>
                  <td className="text-sm">
                    {row.reportedVersion ?? '—'} of {row.latestVersion ?? '—'}
                  </td>
                  <td className="text-sm">
                    {row.reportedAt === null ? (
                      <span className="muted">never</span>
                    ) : (
                      formatRelative(row.reportedAt)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted text-sm">Times shown in {timezone}.</p>
        </div>
      )}
    </Card>
  );
}
